import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import {
  LayoutGrid,
  Globe,
  PieChart,
  Factory,
  Filter,
  Download,
  Settings,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { getFundamentals } from "../utils/priceData.js";

const COLUMNS = [
  { id: "cmp", label: "CMP", unit: "$", tip: "Current market price" },
  { id: "pe", label: "P/E", unit: "", tip: "Price to Earning" },
  { id: "mcap", label: "Mar Cap", unit: "$B", tip: "Market capitalization" },
  { id: "div", label: "Div Yld", unit: "%", tip: "Dividend yield" },
  { id: "np", label: "NP Qtr", unit: "$M", tip: "Net profit, latest quarter" },
  { id: "npVar", label: "Qtr Profit Var", unit: "%", tip: "Quarterly profit growth vs. the year-ago quarter" },
  { id: "sales", label: "Sales Qtr", unit: "$M", tip: "Sales, latest quarter" },
  { id: "salesVar", label: "Qtr Sales Var", unit: "%", tip: "Quarterly sales growth vs. the year-ago quarter" },
  { id: "roce", label: "ROCE", unit: "%", tip: "Return on capital employed" },
];

// Sample market-cap buckets ($B)
const CAPS = [
  { id: "all", label: "All" },
  { id: "large", label: "Large cap" },
  { id: "mid", label: "Mid cap" },
  { id: "small", label: "Small cap" },
];
const capOf = (mcap) => (mcap >= 500 ? "large" : mcap >= 100 ? "mid" : "small");

const PAGE_SIZE = 25;

export default function IndustryCompanies({ industry, onBack }) {
  const [cap, setCap] = useState("all");
  const [hidden, setHidden] = useState([]);
  const [sort, setSort] = useState({ col: "mcap", dir: "desc" });
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () => industry.companies.map((c) => ({ ...c, f: getFundamentals(c.ticker) })),
    [industry]
  );

  const filtered = rows.filter((r) => cap === "all" || capOf(r.f.mcap) === cap);

  const sorted = [...filtered].sort((a, b) => {
    const av = sort.col === "company" ? a.name : a.f[sort.col];
    const bv = sort.col === "company" ? b.name : b.f[sort.col];
    const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visibleRows = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const visibleCols = COLUMNS.filter((c) => !hidden.includes(c.id));

  const toggleSort = (col) => {
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }
    );
    setPage(1);
  };

  const toggleColumn = (id) =>
    setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));

  const exportCsv = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = ["S.No.", "Company", "Ticker", ...visibleCols.map((c) => `${c.label} ${c.unit}`.trim())];
    const lines = sorted.map((r, i) =>
      [i + 1, r.name, r.ticker, ...visibleCols.map((c) => r.f[c.id].toFixed(2))].map(esc).join(",")
    );
    const blob = new Blob([[header.map(esc).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${industry.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ col }) =>
    sort.col === col ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null;

  return (
    <main className="page-bg">
      <div className="dash-main-content wl-wide">
        {/* Breadcrumb */}
        <nav className="wl-breadcrumb">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            <LayoutGrid size={15} /> Industries
          </a>
          <span>/</span>
          <span className="crumb"><Globe size={15} /> {industry.sector}</span>
          <span>/</span>
          <span className="crumb"><PieChart size={15} /> {industry.group}</span>
          <span>/</span>
          <span className="crumb crumb-current"><Factory size={15} /> {industry.name}</span>
        </nav>

        <div className="card industry-card">
          <div className="industry-head">
            <h1 className="industry-title">{industry.name} Companies</h1>
            <Dropdown>
              <Dropdown.Toggle variant="" className="tool-btn">
                <Filter size={14} /> {CAPS.find((c) => c.id === cap).label.toUpperCase()}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {CAPS.map((c) => (
                  <Dropdown.Item
                    key={c.id}
                    active={cap === c.id}
                    onClick={() => {
                      setCap(c.id);
                      setPage(1);
                    }}
                  >
                    {c.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="industry-subhead">
            <p className="results-note">
              {sorted.length} results found: Showing page {current} of {pages}
            </p>
            <div className="d-flex gap-2">
              <button className="btn tool-btn" onClick={exportCsv}>
                <Download size={15} /> EXPORT
              </button>
              <Dropdown align="end" autoClose="outside">
                <Dropdown.Toggle variant="" className="tool-btn tool-btn-primary">
                  <Settings size={15} /> EDIT COLUMNS
                </Dropdown.Toggle>
                <Dropdown.Menu className="columns-menu">
                  {COLUMNS.map((c) => (
                    <label key={c.id} className="columns-item">
                      <input
                        type="checkbox"
                        checked={!hidden.includes(c.id)}
                        onChange={() => toggleColumn(c.id)}
                      />
                      {c.label} {c.unit}
                    </label>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table wl-table mb-0">
              <thead>
                <tr>
                  <th className="wl-th-plain">S.No.</th>
                  <th>
                    <button className="th-btn" onClick={() => toggleSort("company")}>
                      Company <SortIcon col="company" />
                    </button>
                  </th>
                  {visibleCols.map((c) => (
                    <th key={c.id} className="text-end">
                      <button className="th-btn" title={c.tip} onClick={() => toggleSort(c.id)}>
                        {c.label} <span className="th-unit">{c.unit}</span> <SortIcon col={c.id} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => (
                  <tr key={r.ticker}>
                    <td>{(current - 1) * PAGE_SIZE + i + 1}.</td>
                    <td>
                      <a className="wl-company" href={`#/stock/${r.ticker}`}>{r.name}</a>
                      <span className="text-muted small ms-2">{r.ticker}</span>
                    </td>
                    {visibleCols.map((c) => (
                      <td key={c.id} className="text-end">{r.f[c.id].toFixed(2)}</td>
                    ))}
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="wl-empty text-muted">
                      No companies match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="pager">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`page-btn ${p === current ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <p className="text-muted small wl-note">
            Figures shown are sample data for layout purposes.
          </p>
        </div>
      </div>
    </main>
  );
}
